import { useState, useMemo, useCallback, useEffect } from 'react'
import { getPedidos, getBlingContatos, saveBlingContatos, getBlingContatosMeta, clearBlingContatos, getBlingHistorico } from '../utils/storage'
import { importarContatosBling, isBlingConnected, buscarPedidoBling } from '../utils/bling'
import { ImportarBlingHistorico } from '../components/ImportarBlingHistorico'
import { format, formatDistanceToNow } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { Users, Search, Package, Download, RefreshCw, X, CheckCircle, AlertCircle, Trash2 } from 'lucide-react'

import { fmt, formatTelefone } from '../utils/fmt'

function fmtDate(d) {
  if (!d) return '—'
  try { return format(new Date(d + 'T12:00:00'), "dd/MM/yyyy", { locale: ptBR }) } catch { return '—' }
}

// ── Construção de clientes a partir dos pedidos ───────────────────────────────

function buildClientes(pedidos) {
  const map = {}

  pedidos.forEach(p => {
    const tel = (p.cliente?.contato || '').replace(/\D/g, '')
    const key = tel || p.cliente?.nome || 'desconhecido'
    if (!map[key]) {
      map[key] = {
        key,
        nome:     p.cliente?.nome    || '—',
        contato:  p.cliente?.contato || '',
        endereco: p.cliente?.endereco || '',
        cidade:   p.cliente?.cidade  || '',
        bairro:   p.cliente?.bairro  || '',
        pedidos:  [],
      }
    }
    if (p.cliente?.nome) map[key].nome = p.cliente.nome
    map[key].pedidos.push(p)
  })

  return Object.values(map).map(c => {
    const ords = [...c.pedidos].sort((a, b) => {
      const da = a.dataVenda || (a.criadoEm || '').split('T')[0] || ''
      const db = b.dataVenda || (b.criadoEm || '').split('T')[0] || ''
      return da.localeCompare(db)
    })

    const totalGasto    = c.pedidos.reduce((s, p) => s + (p.valorTotal || 0), 0)
    const primeiraCompra = ords[0]?.dataVenda || (ords[0]?.criadoEm || '').split('T')[0] || null
    const ultimaCompra   = ords[ords.length - 1]?.dataVenda || (ords[ords.length - 1]?.criadoEm || '').split('T')[0] || null
    const diasDesdeUltima = ultimaCompra
      ? Math.floor((Date.now() - new Date(ultimaCompra + 'T12:00:00')) / 86_400_000)
      : 9999

    return {
      ...c,
      pedidos: ords,
      qtdPedidos: ords.length,
      totalGasto,
      ticketMedio: totalGasto / ords.length,
      primeiraCompra,
      ultimaCompra,
      diasDesdeUltima,
    }
  })
}

// Detecta nomes em CAIXA ALTA (outra marca no mesmo ERP)
function isCaixaAlta(nome) {
  const letras = nome.replace(/[^a-zA-ZÀ-ÿ]/g, '')
  return letras.length > 0 && letras === letras.toUpperCase()
}

// ── RFM ───────────────────────────────────────────────────────────────────────

// Recebe posição percentil pré-calculada (evita re-ordenar para cada cliente)
function calcRFM(c, totalClientes, posicaoMonetaria) {
  const R = c.diasDesdeUltima <= 30  ? 5
          : c.diasDesdeUltima <= 90  ? 4
          : c.diasDesdeUltima <= 180 ? 3
          : c.diasDesdeUltima <= 365 ? 2 : 1

  const F = c.qtdPedidos >= 5 ? 5
          : c.qtdPedidos === 4 ? 4
          : c.qtdPedidos === 3 ? 3
          : c.qtdPedidos === 2 ? 2 : 1

  const pct = totalClientes > 1 ? posicaoMonetaria / (totalClientes - 1) : 1
  const M   = pct >= 0.8 ? 5 : pct >= 0.6 ? 4 : pct >= 0.4 ? 3 : pct >= 0.2 ? 2 : 1

  return { R, F, M }
}

function getSegmento({ R, F, M }) {
  const avg = (R + F + M) / 3
  if (R >= 4 && F >= 4 && M >= 4) return { label: 'Campeão',   cor: '#CC8800', bg: '#FFF9C2' }
  if (F >= 3 && avg >= 3.5)       return { label: 'Fiel',      cor: '#16A34A', bg: '#DCFCE7' }
  if (R >= 4 && F <= 2)           return { label: 'Promissor', cor: '#2563EB', bg: '#DBEAFE' }
  if (F === 1 && R >= 3)          return { label: 'Novo',      cor: '#0891B2', bg: '#CFFAFE' }
  if (R <= 2 && F >= 2)           return { label: 'Em risco',  cor: '#EA580C', bg: '#FFEDD5' }
  if (R === 1)                    return { label: 'Inativo',   cor: '#64748B', bg: '#F1F5F9' }
  return                                 { label: 'Regular',   cor: '#7C3AED', bg: '#EDE9FE' }
}

// LTV projetado: ticket médio × freq. anual estimada × 3 anos
function calcLTV(c) {
  if (c.qtdPedidos < 2 || !c.primeiraCompra || !c.ultimaCompra) return c.totalGasto
  const diasSpan = Math.max(
    (new Date(c.ultimaCompra + 'T12:00:00') - new Date(c.primeiraCompra + 'T12:00:00')) / 86_400_000,
    1
  )
  const freqAnual = (c.qtdPedidos / diasSpan) * 365
  return c.ticketMedio * freqAnual * 3
}

// ── Normalizar pedido do Bling para o formato interno ────────────────────────

function normalizarPedidoBling(p) {
  const total     = Number(p.totalVenda ?? p.total ?? p.totais?.total ?? p.valor ?? 0)
  const prazo     = p.dataPrevista && p.dataPrevista !== '0000-00-00' ? p.dataPrevista : null
  const dataSaida = p.dataSaida    && p.dataSaida    !== '0000-00-00' ? p.dataSaida    : null
  return {
    id:         `bling-${p.id}`,
    numero:     p.numeroLoja || `BL-${p.numero || p.id}`,
    numeroErp:  p.numero     || null,
    dataVenda:  p.data       || null,
    dataSaida,
    prazo,
    valorTotal: total,
    status:     'BLING',
    origem:     'bling',
    cliente: {
      nome:    p.contato?.nome || '—',
      contato: p.contato?.celular || p.contato?.telefone || '',
    },
    moveis: (p.itens || []).map(item => ({
      modelo: item.descricao || item.codigo || 'Item',
      cor:    item.unidade   || '',
    })),
  }
}

// ── Normalizar contato do Bling ───────────────────────────────────────────────

function normalizarBling(c) {
  const end = c.endereco?.geral || {}
  const tel = (c.celular || c.telefone || '').replace(/\D/g, '')
  return {
    blingId:   c.id,
    nome:      c.nome || '—',
    contato:   c.celular || c.telefone || '',
    tel,
    email:     c.email || '',
    documento: c.numeroDocumento || '',
    rua:       end.endereco   || '',
    numero:    end.numero     || '',
    bairro:    end.bairro     || '',
    cidade:    end.municipio  || '',
    uf:        end.uf         || '',
    cep:       end.cep        || '',
  }
}

// Mescla clientes dos pedidos com contatos importados do Bling
// Resultado: clientes com pedidos (enriquecidos com dados Bling) + contatos Bling sem pedidos
function mesclarComBling(clientesPedidos, blingRaw) {
  const blingNorm  = blingRaw.map(normalizarBling)
  // Map por telefone para lookup O(1) em vez de find() O(n) por cliente
  const blingPorTel = new Map()
  blingNorm.forEach(b => { if (b.tel) blingPorTel.set(b.tel, b) })

  const resultado = clientesPedidos.map(c => {
    const tel   = c.contato.replace(/\D/g, '')
    const match = tel ? blingPorTel.get(tel) || null : null
    return { ...c, bling: match }
  })

  // Rastreia todas as chaves já presentes (telefone OU key gerada)
  const chavesUsadas = new Set(resultado.map(c => c.key))
  const telsUsados   = new Set(resultado.map(c => c.contato.replace(/\D/g, '')).filter(Boolean))

  // Adiciona contatos Bling que não têm nenhum pedido, sem duplicar
  blingNorm.forEach(b => {
    const chave = b.tel || `bling-${b.blingId}`
    if (chavesUsadas.has(chave))      return // chave já existe
    if (b.tel && telsUsados.has(b.tel)) return // telefone já usado

    chavesUsadas.add(chave)
    if (b.tel) telsUsados.add(b.tel)

    resultado.push({
      key:             chave,
      nome:            b.nome,
      contato:         b.contato,
      endereco:        b.rua ? `${b.rua}${b.numero ? ', ' + b.numero : ''}` : '',
      cidade:          b.cidade,
      bairro:          b.bairro,
      pedidos:         [],
      qtdPedidos:      0,
      totalGasto:      0,
      ticketMedio:     0,
      primeiraCompra:  null,
      ultimaCompra:    null,
      diasDesdeUltima: 9999,
      bling:           b,
      semPedidos:      true,
    })
  })

  return resultado
}

// ── Barra RFM ─────────────────────────────────────────────────────────────────

function RFMBar({ label, value, cor, descricao }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div>
          <span className="text-[11px] font-bold text-slate-600 uppercase tracking-wide">{label}</span>
          {descricao && <span className="text-[10px] text-slate-400 ml-1.5">{descricao}</span>}
        </div>
        <span className="text-sm font-black" style={{ color: cor }}>{value}<span className="text-[10px] text-slate-400 font-normal">/5</span></span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${value * 20}%`, backgroundColor: cor }}
        />
      </div>
    </div>
  )
}

// ── KPI Card ──────────────────────────────────────────────────────────────────

function KPI({ label, valor, sub, dark }) {
  return (
    <div className={`rounded-xl p-3.5 ${dark ? 'bg-[#111111]' : 'bg-white border border-slate-200'}`}>
      <p className={`text-[10px] uppercase tracking-wide mb-0.5 ${dark ? 'text-white/40' : 'text-slate-400'}`}>{label}</p>
      <p className={`text-xl font-black leading-tight ${dark ? 'text-[#F5C200]' : 'text-slate-800'}`}>{valor}</p>
      {sub && <p className={`text-[10px] mt-0.5 ${dark ? 'text-white/30' : 'text-slate-400'}`}>{sub}</p>}
    </div>
  )
}

// ── Item da lista ─────────────────────────────────────────────────────────────

function ClienteItem({ c, ativo, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left px-4 py-3 border-b border-slate-100 transition-colors
        ${ativo ? 'bg-[#FFFDF5] border-l-[3px] border-l-[#F5C200]' : 'hover:bg-slate-50'}`}
    >
      <div className="flex items-center gap-2.5">
        <div className="w-8 h-8 rounded-full bg-slate-200 text-slate-600 text-xs font-black flex items-center justify-center shrink-0">
          {c.nome.charAt(0).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-1">
            <p className="text-sm font-semibold text-slate-800 truncate">{c.nome}</p>
            <div className="flex items-center gap-1 shrink-0">
              {c.bling && <span className="text-[9px] font-bold px-1 py-0.5 rounded bg-[#DBEAFE] text-[#1D4ED8]">B</span>}
              <span
                className="text-[9px] font-bold px-1.5 py-0.5 rounded-full"
                style={{ backgroundColor: c.segmento.bg, color: c.segmento.cor }}
              >
                {c.segmento.label}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-0.5 text-[10px] text-slate-400">
            <span className="font-semibold text-slate-600">{fmt(c.totalGasto)}</span>
            <span>·</span>
            <span>{c.qtdPedidos} pedido{c.qtdPedidos !== 1 ? 's' : ''}</span>
            <span>·</span>
            <span>{c.diasDesdeUltima < 9999 ? `${c.diasDesdeUltima}d atrás` : '—'}</span>
          </div>
        </div>
      </div>
    </button>
  )
}

// ── Detalhe do cliente ────────────────────────────────────────────────────────

const STATUS_LABEL = {
  A_FABRICAR: 'A Fabricar', FABRICANDO: 'Fabricando', FABRICADO: 'Fabricado',
  EXPEDICAO: 'Expedição', ROTA: 'Rota', POS_VENDA: 'Pós-venda', BLING: 'Bling',
}

function ClienteDetalhe({ c }) {
  const ltv = calcLTV(c)

  // Busca itens dos pedidos Bling que chegaram sem descrição
  const [itensBling, setItensBling] = useState({}) // { [pedidoId]: [...itens] }
  const [carregando,  setCarregando] = useState(false)

  useEffect(() => {
    if (!isBlingConnected()) return
    const semItens = c.pedidos.filter(p => p.origem === 'bling' && !(p.moveis?.length))
    if (!semItens.length) return

    let cancelado = false
    setCarregando(true)
    setItensBling({})

    ;(async () => {
      for (const pedido of semItens) {
        if (cancelado) break
        const blingId = String(pedido.id).replace('bling-', '')
        try {
          const detalhe = await buscarPedidoBling(blingId)
          if (detalhe?.itens?.length && !cancelado) {
            setItensBling(prev => ({ ...prev, [pedido.id]: detalhe.itens }))
          }
        } catch {}
        await new Promise(r => setTimeout(r, 200))
      }
      if (!cancelado) setCarregando(false)
    })()

    return () => { cancelado = true }
  }, [c.key])

  const rfmDescricao = {
    R: c.diasDesdeUltima < 9999 ? `${c.diasDesdeUltima} dias desde a última compra` : 'sem data',
    F: `${c.qtdPedidos} compra${c.qtdPedidos !== 1 ? 's' : ''} no total`,
    M: `total gasto: ${fmt(c.totalGasto)}`,
  }

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="p-6 max-w-2xl">

        {/* Cabeçalho do cliente */}
        <div className="flex items-start gap-4 mb-6">
          <div className="w-14 h-14 rounded-2xl bg-[#111111] text-[#F5C200] text-2xl font-black flex items-center justify-center shrink-0">
            {c.nome.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-xl font-bold text-slate-800">{c.nome}</h2>
              <span
                className="text-xs font-bold px-2.5 py-1 rounded-full"
                style={{ backgroundColor: c.segmento.bg, color: c.segmento.cor }}
              >
                {c.segmento.label}
              </span>
            </div>
            {c.contato && <p className="text-sm text-slate-500 mt-0.5">{formatTelefone(c.contato)}</p>}
            {(c.bairro || c.cidade) && (
              <p className="text-xs text-slate-400 mt-0.5">
                {[c.bairro, c.cidade].filter(Boolean).join(' · ')}
              </p>
            )}
            <div className="flex gap-3 mt-2 text-[11px] text-slate-400">
              <span>Primeira compra: <strong className="text-slate-600">{fmtDate(c.primeiraCompra)}</strong></span>
              <span>·</span>
              <span>Última: <strong className="text-slate-600">{fmtDate(c.ultimaCompra)}</strong></span>
            </div>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <KPI dark label="LTV Histórico" valor={fmt(c.totalGasto)} sub={`${c.qtdPedidos} pedido${c.qtdPedidos !== 1 ? 's' : ''} realizados`} />
          <KPI label="LTV Projetado (3a)" valor={fmt(ltv)} sub={c.qtdPedidos >= 2 ? 'baseado na frequência real' : 'dados insuficientes para projeção'} />
          <KPI label="Ticket Médio" valor={fmt(c.ticketMedio)} sub="por pedido" />
          <KPI label="Última Compra" valor={c.diasDesdeUltima < 9999 ? `${c.diasDesdeUltima}d` : '—'} sub={fmtDate(c.ultimaCompra)} />
        </div>

        {/* Análise RFM */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-bold text-slate-800">Análise RFM</h3>
              <p className="text-[10px] text-slate-400 mt-0.5">Recência · Frequência · Monetário</p>
            </div>
            <div className="flex items-center gap-1.5">
              {[
                { v: c.rfm.R, cor: '#3B82F6', title: 'R' },
                { v: c.rfm.F, cor: '#22C55E', title: 'F' },
                { v: c.rfm.M, cor: '#F5C200', title: 'M' },
              ].map(({ v, cor, title }) => (
                <div key={title} className="flex flex-col items-center">
                  <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center text-white text-sm font-black shadow-sm"
                    style={{ backgroundColor: cor }}
                  >
                    {v}
                  </div>
                  <span className="text-[9px] text-slate-400 mt-0.5 font-bold">{title}</span>
                </div>
              ))}
              <div className="ml-1 w-9 h-9 rounded-xl bg-[#111111] flex items-center justify-center text-[#F5C200] text-sm font-black shadow-sm">
                {((c.rfm.R + c.rfm.F + c.rfm.M) / 3).toFixed(1)}
              </div>
            </div>
          </div>

          <div className="space-y-3.5">
            <RFMBar label="Recência"   value={c.rfm.R} cor="#3B82F6" descricao={rfmDescricao.R} />
            <RFMBar label="Frequência" value={c.rfm.F} cor="#22C55E" descricao={rfmDescricao.F} />
            <RFMBar label="Monetário"  value={c.rfm.M} cor="#F5C200" descricao={rfmDescricao.M} />
          </div>

          {/* Insight do segmento */}
          <div
            className="mt-4 rounded-xl p-3 text-xs font-medium"
            style={{ backgroundColor: c.segmento.bg, color: c.segmento.cor }}
          >
            {c.segmento.label === 'Campeão'   && '🏆 Cliente de altíssimo valor — priorize atendimento e ofereça lançamentos em primeira mão.'}
            {c.segmento.label === 'Fiel'      && '💚 Compra com frequência e gasta bem — fortaleça o relacionamento com acompanhamento pós-venda.'}
            {c.segmento.label === 'Promissor' && '🌱 Comprou recentemente e tem potencial — mantenha contato para incentivar a próxima compra.'}
            {c.segmento.label === 'Novo'      && '✨ Cliente novo — primeira impressão é crucial. Garanta uma excelente entrega.'}
            {c.segmento.label === 'Em risco'  && '⚠️ Já comprou bastante, mas faz tempo. Vale entrar em contato para reativar.'}
            {c.segmento.label === 'Inativo'   && '😴 Sem compra há mais de 1 ano. Considere uma ação de reengajamento.'}
            {c.segmento.label === 'Regular'   && '👤 Perfil intermediário — acompanhe a próxima oportunidade de compra.'}
          </div>
        </div>

        {/* Histórico de pedidos */}
        <div>
          <h3 className="text-sm font-bold text-slate-800 mb-3">
            Histórico de pedidos
            <span className="ml-2 text-[11px] font-normal text-slate-400">{c.qtdPedidos} pedido{c.qtdPedidos !== 1 ? 's' : ''}</span>
          </h3>

          {carregando && (
            <p className="text-[10px] text-slate-400 flex items-center gap-1.5 mb-2">
              <RefreshCw size={10} className="animate-spin" /> Buscando descrição dos produtos…
            </p>
          )}

          <div className="space-y-2.5">
            {[...c.pedidos].reverse().map((p, i) => {
              const isBling   = p.origem === 'bling'
              const isRecente = i === 0
              // Usa itens buscados on-demand se disponíveis; senão usa os do pedido
              const itensRaw  = itensBling[p.id] || p.moveis || []
              const itens     = itensBling[p.id]
                ? itensRaw.map(it => ({ modelo: it.descricao || it.codigo || '', cor: `${it.quantidade > 1 ? `×${it.quantidade}` : ''}` })).filter(m => m.modelo)
                : itensRaw.filter(m => m.modelo && m.modelo !== 'Item')
              return (
                <div
                  key={p.id}
                  className={`p-3.5 rounded-xl border transition-colors
                    ${isRecente ? 'bg-[#FFFDF5] border-[#F5C200]/30' : 'bg-white border-slate-100'}`}
                >
                  {/* Linha 1: número + status + badge recente */}
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2">
                      <Package size={12} className="text-slate-300 shrink-0" />
                      <span className="text-[11px] font-mono font-semibold text-slate-500">{p.numero}</span>
                      {p.numeroErp && p.numeroErp !== p.numero && (
                        <span className="text-[9px] text-slate-300 font-mono">#{p.numeroErp}</span>
                      )}
                      {isRecente && (
                        <span className="text-[9px] font-bold text-[#CC8800] uppercase tracking-wide bg-[#FFF9C2] px-1.5 py-0.5 rounded-full">Último</span>
                      )}
                    </div>
                    <span
                      className="text-[10px] font-bold px-1.5 py-0.5 rounded-full shrink-0"
                      style={{ backgroundColor: isBling ? '#DBEAFE' : c.segmento.bg, color: isBling ? '#1D4ED8' : c.segmento.cor }}
                    >
                      {STATUS_LABEL[p.status] || p.status}
                    </span>
                  </div>

                  {/* Linha 2: itens/móveis */}
                  {itens.length > 0 ? (
                    <div className="space-y-0.5 mb-2">
                      {itens.map((m, idx) => (
                        <p key={idx} className="text-xs text-slate-700 leading-snug font-medium">
                          {[m.modelo, m.cor].filter(Boolean).join(' — ')}
                        </p>
                      ))}
                    </div>
                  ) : (
                    p.observacoesGerais && (
                      <p className="text-xs text-slate-500 italic mb-2 leading-snug">{p.observacoesGerais}</p>
                    )
                  )}

                  {/* Linha 3: datas + vendedora + valor */}
                  <div className="flex items-end justify-between gap-3 mt-1.5">
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-3 flex-wrap">
                        {p.dataVenda && (
                          <span className="text-[10px] text-slate-400">
                            Venda: <strong className="text-slate-600">{fmtDate(p.dataVenda)}</strong>
                          </span>
                        )}
                        {p.prazo && (
                          <span className="text-[10px] text-slate-400">
                            Prazo: <strong className="text-slate-600">{fmtDate(p.prazo)}</strong>
                          </span>
                        )}
                        {p.dataSaida && (
                          <span className="text-[10px] text-slate-400">
                            Saída: <strong className="text-slate-600">{fmtDate(p.dataSaida)}</strong>
                          </span>
                        )}
                      </div>
                      {p.vendedora && (
                        <span className="text-[10px] text-slate-400">
                          Vendedora: <strong className="text-slate-600">{p.vendedora}</strong>
                        </span>
                      )}
                    </div>
                    <span className="text-base font-black text-[#CC8800] shrink-0">{fmt(p.valorTotal)}</span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

      </div>
    </div>
  )
}

// ── Ficha para contato Bling sem pedidos ─────────────────────────────────────

function ClienteSemPedidos({ c }) {
  const b = c.bling
  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="max-w-lg">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-14 h-14 rounded-2xl bg-slate-200 text-slate-500 text-2xl font-black flex items-center justify-center shrink-0">
            {c.nome.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h2 className="text-xl font-bold text-slate-800">{c.nome}</h2>
              <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-[#DBEAFE] text-[#1D4ED8]">Bling</span>
              <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Sem pedidos</span>
            </div>
            {c.contato && <p className="text-sm text-slate-500">{formatTelefone(c.contato)}</p>}
            {b?.email && <p className="text-xs text-slate-400 mt-0.5">{b.email}</p>}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-3">
          <h3 className="text-sm font-bold text-slate-700 mb-2">Dados do Bling</h3>
          {b?.documento && (
            <div className="flex justify-between text-xs">
              <span className="text-slate-400">CPF / CNPJ</span>
              <span className="font-semibold text-slate-700">{b.documento}</span>
            </div>
          )}
          {(b?.rua || b?.numero) && (
            <div className="flex justify-between text-xs">
              <span className="text-slate-400">Endereço</span>
              <span className="font-semibold text-slate-700 text-right">{[b.rua, b.numero].filter(Boolean).join(', ')}</span>
            </div>
          )}
          {b?.bairro && (
            <div className="flex justify-between text-xs">
              <span className="text-slate-400">Bairro</span>
              <span className="font-semibold text-slate-700">{b.bairro}</span>
            </div>
          )}
          {b?.cidade && (
            <div className="flex justify-between text-xs">
              <span className="text-slate-400">Cidade</span>
              <span className="font-semibold text-slate-700">{b.cidade}{b.uf ? ` — ${b.uf}` : ''}</span>
            </div>
          )}
        </div>

        <p className="text-xs text-slate-400 text-center mt-6">Este contato ainda não tem pedidos no Mini Deck.</p>
      </div>
    </div>
  )
}

// ── Painel vazio (resumo geral) ───────────────────────────────────────────────

function PainelResumo({ clientes }) {
  const totalLTV     = clientes.reduce((s, c) => s + c.totalGasto, 0)
  const totalPedidos = clientes.reduce((s, c) => s + c.qtdPedidos, 0)
  const ticketGeral  = totalPedidos > 0 ? totalLTV / totalPedidos : 0
  const ativos90     = clientes.filter(c => c.diasDesdeUltima <= 90).length
  const emRisco      = clientes.filter(c => c.segmento.label === 'Em risco' || c.segmento.label === 'Inativo').length

  const segmentos = ['Campeão', 'Fiel', 'Promissor', 'Novo', 'Regular', 'Em risco', 'Inativo']
  const segCores  = {
    'Campeão':   { cor: '#CC8800', bg: '#FFF9C2' },
    'Fiel':      { cor: '#16A34A', bg: '#DCFCE7' },
    'Promissor': { cor: '#2563EB', bg: '#DBEAFE' },
    'Novo':      { cor: '#0891B2', bg: '#CFFAFE' },
    'Regular':   { cor: '#7C3AED', bg: '#EDE9FE' },
    'Em risco':  { cor: '#EA580C', bg: '#FFEDD5' },
    'Inativo':   { cor: '#64748B', bg: '#F1F5F9' },
  }

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="max-w-2xl">

        <h2 className="text-lg font-bold text-slate-800 mb-1">Visão geral da base</h2>
        <p className="text-sm text-slate-400 mb-6">Selecione um cliente para ver a ficha completa</p>

        {/* KPIs gerais */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <KPI dark label="LTV Total da Base" valor={fmt(totalLTV)} sub={`${clientes.length} clientes únicos`} />
          <KPI label="Ticket Médio Geral" valor={fmt(ticketGeral)} sub="por pedido" />
          <KPI label="Ativos (90 dias)" valor={ativos90} sub={`${Math.round(ativos90 / Math.max(clientes.length, 1) * 100)}% da base`} />
          <KPI label="Em risco / Inativos" valor={emRisco} sub="precisam de atenção" />
        </div>

        {/* Distribuição por segmento */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <h3 className="text-sm font-bold text-slate-800 mb-4">Distribuição por segmento RFM</h3>
          <div className="space-y-2.5">
            {segmentos.map(seg => {
              const count = clientes.filter(c => c.segmento.label === seg).length
              if (count === 0) return null
              const pct  = Math.round(count / clientes.length * 100)
              const s    = segCores[seg]
              return (
                <div key={seg}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-slate-700">{seg}</span>
                    <span className="text-xs font-bold" style={{ color: s.cor }}>{count} cliente{count !== 1 ? 's' : ''} · {pct}%</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-700"
                      style={{ width: `${pct}%`, backgroundColor: s.cor }}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        </div>

      </div>
    </div>
  )
}

// ── Barra de importação ───────────────────────────────────────────────────────

function BarraImportacao({ meta, importando, progresso, erro, onImportar, onLimpar }) {
  const blingOk = isBlingConnected()

  const tempoAtras = meta
    ? formatDistanceToNow(new Date(meta.importadoEm), { locale: ptBR, addSuffix: true })
    : null

  if (importando) {
    return (
      <div className="mx-3 my-2 px-3 py-2 bg-[#FFF9C2] border border-[#F5C200]/40 rounded-xl">
        <div className="flex items-center gap-2">
          <RefreshCw size={12} className="text-[#CC8800] animate-spin shrink-0" />
          <span className="text-xs font-semibold text-[#CC8800]">Importando…</span>
          <span className="text-xs text-slate-500 ml-auto">{progresso} contatos</span>
        </div>
        <div className="mt-1.5 h-1 bg-[#F5C200]/20 rounded-full overflow-hidden">
          <div className="h-full bg-[#F5C200] rounded-full animate-pulse w-full" />
        </div>
      </div>
    )
  }

  if (erro) {
    return (
      <div className="mx-3 my-2 px-3 py-2 bg-red-50 border border-red-200 rounded-xl">
        <div className="flex items-start gap-2">
          <AlertCircle size={12} className="text-red-500 shrink-0 mt-0.5" />
          <p className="text-xs text-red-700 flex-1">{erro}</p>
          <button onClick={onImportar} className="text-[10px] text-red-500 hover:underline shrink-0">Tentar</button>
        </div>
      </div>
    )
  }

  if (meta) {
    return (
      <div className="mx-3 my-2 px-3 py-2 bg-green-50 border border-green-200 rounded-xl">
        <div className="flex items-center gap-2">
          <CheckCircle size={12} className="text-green-600 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-semibold text-green-800">{meta.total} contatos importados</p>
            <p className="text-[9px] text-green-600">{tempoAtras}</p>
          </div>
          <button onClick={onImportar} title="Reimportar" className="p-1 rounded hover:bg-green-100 text-green-600">
            <RefreshCw size={11} />
          </button>
          <button onClick={onLimpar} title="Remover importação" className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-500">
            <Trash2 size={11} />
          </button>
        </div>
      </div>
    )
  }

  if (!blingOk) {
    return (
      <div className="mx-3 my-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl">
        <p className="text-[10px] text-slate-400 text-center">Conecte o Bling em <strong>Config</strong> para importar contatos</p>
      </div>
    )
  }

  return (
    <div className="mx-3 my-2">
      <button
        onClick={onImportar}
        className="w-full flex items-center justify-center gap-1.5 py-2 text-xs font-semibold text-[#CC8800] bg-[#FFF9C2] hover:bg-[#F5C200]/30 border border-[#F5C200]/40 rounded-xl transition-colors"
      >
        <Download size={13} />
        Importar contatos do Bling
      </button>
    </div>
  )
}

// ── Página principal ──────────────────────────────────────────────────────────

export function Clientes() {
  const [busca,       setBusca]       = useState('')
  const [selecionado, setSelecionado] = useState(null)
  const [ordenar,     setOrdenar]     = useState('ltv')
  const [visiveis,    setVisiveis]    = useState(120)
  const [importando,  setImportando]  = useState(false)
  const [progresso,   setProgresso]   = useState(0)
  const [erro,        setErro]        = useState(null)
  const [blingRaw,    setBlingRaw]    = useState(() => getBlingContatos())
  const [meta,        setMeta]        = useState(() => getBlingContatosMeta())

  const handleImportar = useCallback(async () => {
    setImportando(true)
    setErro(null)
    setProgresso(0)
    try {
      const contatos = await importarContatosBling(n => setProgresso(n))
      saveBlingContatos(contatos)
      setBlingRaw(contatos)
      setMeta(getBlingContatosMeta())
    } catch (e) {
      setErro(e.message)
    } finally {
      setImportando(false)
    }
  }, [])

  const handleLimpar = useCallback(() => {
    clearBlingContatos()
    setBlingRaw([])
    setMeta(null)
    setErro(null)
  }, [])

  const [blingPedidosRaw, setBlingPedidosRaw] = useState(() => getBlingHistorico())

  const pedidos = useMemo(() => {
    // Exclui pedidos fantasmas de ajuste (AJ-) e pedidos filhos divididos.
    // Não contam pra ticket médio, LTV, RFM nem qtd de pedidos do cliente.
    const locais        = getPedidos().filter(p => !p.tipoAjuste && !p.tipoFilho)
    const numerosLocais = new Set(locais.map(p => p.numero))
    const doBling       = blingPedidosRaw
      .map(p => normalizarPedidoBling(p))
      .filter(p => !numerosLocais.has(p.numero)) // evita duplicar pedidos já no app
    return [...locais, ...doBling]
  }, [blingPedidosRaw])

  const base = useMemo(() => buildClientes(pedidos), [pedidos])

  // Mescla com Bling e calcula RFM
  const clientes = useMemo(() => {
    const mesclados  = mesclarComBling(base, blingRaw)
    const comPedidos = mesclados.filter(c => !c.semPedidos)

    // Pré-ordena UMA vez para calcular percentil monetário → O(n log n) total
    const sortedM       = [...comPedidos].sort((a, b) => a.totalGasto - b.totalGasto)
    const posicaoPorKey = new Map(sortedM.map((c, i) => [c.key, i]))
    const totalComPed   = comPedidos.length

    return mesclados.filter(c => !isCaixaAlta(c.nome)).map(c => {
      if (c.semPedidos) {
        return { ...c, rfm: { R: 0, F: 0, M: 0 }, segmento: { label: 'Sem pedidos', cor: '#94A3B8', bg: '#F1F5F9' } }
      }
      const rfm = calcRFM(c, totalComPed, posicaoPorKey.get(c.key) ?? 0)
      return { ...c, rfm, segmento: getSegmento(rfm) }
    })
  }, [base, blingRaw])

  const filtrados = useMemo(() => {
    setVisiveis(120) // reseta paginação ao filtrar/ordenar
    let lista = clientes
    if (busca.trim()) {
      const q = busca.toLowerCase()
      lista   = lista.filter(c =>
        c.nome.toLowerCase().includes(q) ||
        c.contato.includes(q) ||
        (c.cidade || '').toLowerCase().includes(q)
      )
    }
    return [...lista].sort((a, b) => {
      if (ordenar === 'ltv')      return b.totalGasto - a.totalGasto
      if (ordenar === 'recencia') return a.diasDesdeUltima - b.diasDesdeUltima
      if (ordenar === 'freq')     return b.qtdPedidos - a.qtdPedidos
      return 0
    })
  }, [clientes, busca, ordenar])

  const clienteSel = selecionado ? clientes.find(c => c.key === selecionado) : null

  return (
    <div className="flex h-[calc(100vh-64px)]">

      {/* ── Lista lateral ─────────────────────────────────────────────── */}
      <div className="w-72 shrink-0 border-r border-slate-200 flex flex-col bg-white">

        <div className="p-3 border-b border-slate-100 space-y-2">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold text-slate-800 flex items-center gap-1.5">
              <Users size={14} className="text-[#CC8800]" />
              Clientes
            </h2>
            <span className="text-[10px] text-slate-400 font-mono bg-slate-100 px-1.5 py-0.5 rounded-full">
              {filtrados.length}
            </span>
          </div>

          {/* Busca */}
          <div className="relative">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              value={busca}
              onChange={e => setBusca(e.target.value)}
              placeholder="Buscar por nome ou telefone..."
              className="w-full pl-7 pr-2 py-1.5 text-xs border border-slate-200 rounded-lg outline-none focus:border-[#F5C200] bg-slate-50"
            />
          </div>

          {/* Ordenação */}
          <div className="flex gap-1">
            {[
              { k: 'ltv',      label: 'LTV'     },
              { k: 'recencia', label: 'Recente' },
              { k: 'freq',     label: 'Freq.'   },
            ].map(o => (
              <button
                key={o.k}
                onClick={() => setOrdenar(o.k)}
                className={`flex-1 text-[10px] font-bold py-1 rounded-lg transition-colors
                  ${ordenar === o.k ? 'bg-[#111111] text-[#F5C200]' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>

        {/* Importar contatos Bling */}
        <BarraImportacao
          meta={meta}
          importando={importando}
          progresso={progresso}
          erro={erro}
          onImportar={handleImportar}
          onLimpar={handleLimpar}
        />

        {/* Importar pedidos Bling (base para LTV/RFM) */}
        <div className="px-3 pb-2">
          <ImportarBlingHistorico
            compact
            onImportado={() => setBlingPedidosRaw(getBlingHistorico())}
          />
        </div>

        <div className="flex-1 overflow-y-auto">
          {filtrados.length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-10">Nenhum cliente encontrado</p>
          ) : (
            <>
              {filtrados.slice(0, visiveis).map(c => (
                <ClienteItem
                  key={c.key}
                  c={c}
                  ativo={selecionado === c.key}
                  onClick={() => setSelecionado(c.key)}
                />
              ))}
              {visiveis < filtrados.length && (
                <button
                  onClick={() => setVisiveis(v => v + 120)}
                  className="w-full py-3 text-xs font-semibold text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-colors border-t border-slate-100"
                >
                  Carregar mais ({filtrados.length - visiveis} restantes)
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Painel direito ────────────────────────────────────────────── */}
      <div className="flex-1 bg-slate-50 flex overflow-hidden">
        {clienteSel
          ? clienteSel.semPedidos
            ? <ClienteSemPedidos c={clienteSel} />
            : <ClienteDetalhe c={clienteSel} />
          : <PainelResumo clientes={clientes} />
        }
      </div>
    </div>
  )
}
